import { forwardRef, useState, Fragment, useEffect } from 'react'
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import { format, parseISO } from 'date-fns'

import useDates from 'services/hooks/useDate'
import { useGetBigCalendar } from 'services/api/modules/leave/calendar'
import DayDiv from './dayDiv'
import CalendarDrawer from './calendarDrawer'

const dayofWeek = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']

const MonthlyCalendar = forwardRef(({ calendarValue, filters, apiFilter }, ref) => {
  const { currentMonth } = useDates({ date: calendarValue })
  const { data, isLoading, refetch } = useGetBigCalendar(apiFilter, calendarValue)
  const [drawerState, setDrawerState] = useState({ right: false })
  const [drawerData, setDrawerData] = useState(new Date())
  const handleDrawer = (anchor, open) => () => {
    setDrawerState({ ...drawerState, [anchor]: open })
  }

  const [...calendarEvent] = Object.entries(data?.data || {})

  const [tabState, setTabState] = useState(0)

  const theme = useTheme()
  const sm = useMediaQuery(theme.breakpoints.up('sm'))
  const mobile_l = useMediaQuery(theme.breakpoints.up('mobile_l'))

  function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase()
  }

  useEffect(() => {
    refetch()
  }, [calendarValue])

  return (
    <Box width={1} height={1}>
      <Box
        width={1}
        display="flex"
        flexWrap="wrap"
        sx={{
          overflow: 'hidden',
          position: 'sticky',
          top: '0',
          background: 'white',
          zIndex: '1',
          boxShadow: '1px 1px 2px rgb(0 0 0 / 0.10)',
          borderTop: '1px solid #E5E5E5',
          borderLeft: '1px solid #E5E5E5',
          borderRight: '1px solid #E5E5E5',
        }}
      >
        {dayofWeek.map((day) => (
          <Box
            key={day}
            sx={{
              width: 'calc(100% / 7)',
              height: '50px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="body2"
              color="light-gray-text.main"
              sx={{ fontWeight: sm ? 'bold' : '' }}
            >
              {sm ? day : capitalizeFirstLetter(day)}
            </Typography>
          </Box>
        ))}
      </Box>
      <Box
        width={1}
        display="flex"
        flexWrap="wrap"
        sx={{
          borderRight: '1px solid #E5E5E5',
        }}
      >
        {currentMonth.map((day, index) => {
          // This render the boxes before the 1st day of the month
          if (index === 0) {
            const offset = (day.hari - 1 + 7) % 7
            return (
              <Fragment key={index}>
                {Array.from({ length: offset }, (_, k) => (
                  <Box
                    key={k}
                    sx={{
                      width: 'calc(100% / 7)',
                      height: mobile_l ? '180px' : '140px',
                      borderLeft: '1px solid #E5E5E5',
                      borderTop: '1px solid #E5E5E5',
                      background: '#F6F6F6',
                    }}
                  />
                ))}
                <DayDiv
                  day={day}
                  ref={ref}
                  index={index}
                  value={calendarValue}
                  filters={filters}
                  calendarEvent={
                    calendarEvent.length > 0 &&
                    calendarEvent?.find(
                      (item) => format(parseISO(item[0]), 'dd MMMM yyyy') === day.fullDate
                    )
                  }
                  setDrawerData={setDrawerData}
                  drawerState={drawerState}
                  setDrawerState={setDrawerState}
                  setTabState={setTabState}
                  isLoading={isLoading}
                />
              </Fragment>
            )
          }

          // This render the boxes after the final day of the month
          if (index === currentMonth.length - 1) {
            return (
              <Fragment key={index}>
                <DayDiv
                  day={day}
                  ref={ref}
                  index={index}
                  value={calendarValue}
                  filters={filters}
                  calendarEvent={
                    calendarEvent.length > 0 &&
                    calendarEvent?.find(
                      (item) => format(parseISO(item[0]), 'dd MMMM yyyy') === day.fullDate
                    )
                  }
                  setDrawerData={setDrawerData}
                  drawerState={drawerState}
                  setDrawerState={setDrawerState}
                  setTabState={setTabState}
                  isLoading={isLoading}
                />
                {day.hari !== 0
                  ? Array.from({ length: 7 - day.hari }, (_, k) => (
                      <Box
                        key={k}
                        sx={{
                          width: 'calc(100% / 7)',
                          height: mobile_l ? '180px' : '140px',
                          borderLeft: '1px solid #E5E5E5',
                          borderTop: '1px solid #E5E5E5',
                          background: '#F6F6F6',
                        }}
                      />
                    ))
                  : null}
              </Fragment>
            )
          }
          // This render all the boxes except 1st and last day of the month
          return (
            <DayDiv
              key={index}
              day={day}
              index={index}
              ref={ref}
              filters={filters}
              value={calendarValue}
              calendarEvent={
                calendarEvent.length > 0 &&
                calendarEvent?.find(
                  (item) => format(parseISO(item[0]), 'dd MMMM yyyy') === day.fullDate
                )
              }
              setDrawerData={setDrawerData}
              drawerState={drawerState}
              setDrawerState={setDrawerState}
              setTabState={setTabState}
              isLoading={isLoading}
            />
          )
        })}
      </Box>
      <CalendarDrawer
        tabState={tabState}
        state={drawerState}
        handleDrawer={handleDrawer}
        data={drawerData}
        setData={setDrawerData}
        calendarEvents={calendarEvent}
      />
    </Box>
  )
})

export default MonthlyCalendar
